import React, { useRef, useState, useEffect, useCallback } from "react";
import axios from '../api/axios';
import { Link, useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import useProps from "../hooks/useProps";
import { EMAIL_REGEX, ENDPOINT_ACCOUNT_REGISTER, PASSWORD_REGEX } from "../Config";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import usePageTitle from "../hooks/usePageTitle";


const RegisterPage = () => {
    usePageTitle('Register');
    const { setAuth } = useAuth();
    const { props, setProps } = useProps();
    const emailRef = useRef();
    const errRef = useRef();
    const navigate = useNavigate();

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');

    const [email, setEmail] = useState('');
    const [validEmail, setValidEmail] = useState(false);
    const [emailFocus, setEmailFocus] = useState(false);

    const [pwd, setPwd] = useState('');
    const [validPwd, setValidPwd] = useState(false);
    const [pwdFocus, setPwdFocus] = useState(false);

    const [matchPwd, setMatchPwd] = useState('');
    const [validMatch, setValidMatch] = useState(false);
    const [matchFocus, setMatchFocus] = useState(false);

    const [errMsg, setErrMsg] = useState('');
    const [success, setSuccess] = useState(false);

    const [submitting, setSubmitting] = useState(false);

    const { executeRecaptcha } = useGoogleReCaptcha();

    useEffect(() => {
        emailRef.current.focus();
    }, [])

    useEffect(() => {
        setValidEmail(EMAIL_REGEX.test(email));
    }, [email])

    useEffect(() => {
        setValidPwd(PASSWORD_REGEX.test(pwd));
        setValidMatch(pwd && pwd === matchPwd);
    }, [pwd, matchPwd])

    useEffect(() => {
        setErrMsg('');
    }, [email, pwd, matchPwd])

    const handleSubmit = async (e) => {
        e.preventDefault();
        // if button enabled with JS hack
        const v1 = EMAIL_REGEX.test(email);
        const v2 = PASSWORD_REGEX.test(pwd);
        if (!v1 || !v2) {
            setErrMsg("Invalid Entry");
            return;
        }
        try {
            setSubmitting(true);
            const recaptchaToken = await executeRecaptcha('register');
            const response = await axios.post(ENDPOINT_ACCOUNT_REGISTER,
                JSON.stringify({
                    fname: firstName,
                    lname: lastName,
                    email: email,
                    password: pwd,
                    recaptchaToken: recaptchaToken
                }),
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true
                }
            );
            // TODO: remove console.logs before deployment
            // console.log(JSON.stringify(response?.data));
            const account = response?.data?.account;
            const accessToken = response?.data?.accessToken;
            setAuth({ account, accessToken });
            //console.log(JSON.stringify(response))
            setSuccess(true);
            //clear state and controlled inputs
            setEmail('');
            setPwd('');
            setMatchPwd('');
            navigate('/', { replace: true });
        } catch (err) {
            if (!err?.response) {
                setErrMsg('No Server Response');
            } else if (err.response?.status === 409) {
                setErrMsg('Username Taken');
            } else {
                setErrMsg(err?.response?.data.data ?? 'Registration Failed')
            }
            errRef.current.focus();
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <section>
            {success ? (
                <section>
                    <h1>Success!</h1>
                    <p>
                        <Link to="/">Sign In</Link>
                    </p>
                </section>
            ) : (
                <div className="row d-flex justify-content-center">
                    <div className="col-md-4">
                        <p ref={errRef} className={errMsg ? "alert alert-danger" : "offscreen"} aria-live="assertive">{errMsg}</p>
                        <h2 className="mb-3">
                            Welcome to VideoBoost!
                        </h2>
                        <p className="text-muted h5 mb-5">To get started, create a login here.</p>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <div className="row">
                                    <div className="col">
                                        <label htmlFor="firstName">
                                            First Name:
                                        </label>
                                        <input
                                            className={`form-control ${firstName.trim().length ? "is-valid" : firstName ? "is-invalid" : ""}`}
                                            type="text"
                                            id="firstName"
                                            onChange={(e) => setFirstName(e.target.value)}
                                            value={firstName}
                                            required
                                            style={{ textTransform: "capitalize" }}
                                        />
                                    </div>
                                    <div className="col">
                                        <label htmlFor="lastName">
                                            Last Name:
                                        </label>
                                        <input
                                            className={`form-control ${lastName.trim().length ? "is-valid" : lastName ? "is-invalid" : ""}`}
                                            type="text"
                                            id="lastName"
                                            onChange={(e) => setLastName(e.target.value)}
                                            value={lastName}
                                            required
                                            style={{ textTransform: "capitalize" }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="mb-3">
                                <label htmlFor="email">
                                    Email:
                                </label>
                                <input
                                    className={`form-control ${validEmail ? "is-valid" : email ? "is-invalid" : ""}`}
                                    type="text"
                                    id="email"
                                    ref={emailRef}
                                    autoComplete="off"
                                    onChange={(e) => setEmail(e.target.value)}
                                    value={email}
                                    required
                                    aria-invalid={validEmail ? "false" : "true"}
                                    aria-describedby="uidnote"
                                    onFocus={() => setEmailFocus(true)}
                                    onBlur={() => setEmailFocus(false)}
                                />
                                <div id="uidnote" className={emailFocus && email && !validEmail ? "invalid-feedback" : "d-none"}>
                                    Invalid email address
                                </div>
                            </div>
                            <div className="mb-3">
                                <label htmlFor="password">
                                    Password:
                                </label>
                                <input
                                    className={`form-control ${validPwd ? "is-valid" : pwd ? "is-invalid" : ""}`}
                                    type="password"
                                    id="password"
                                    onChange={(e) => setPwd(e.target.value)}
                                    value={pwd}
                                    required
                                    aria-invalid={validPwd ? "false" : "true"}
                                    aria-describedby="pwdnote"
                                    onFocus={() => setPwdFocus(true)}
                                    onBlur={() => setPwdFocus(false)}
                                />
                                <small className="form-text text-muted">
                                    Minimum length: 8 characters. Recommended to include a mix of uppercase and lowercase letters, numbers, and special characters for better security.
                                </small>
                            </div>

                            <div className="mb-3">
                                <label htmlFor="confirm_pwd">
                                    Confirm Password:
                                </label>
                                <input
                                    className={`form-control ${validMatch ? "is-valid" : matchPwd ? "is-invalid" : ""}`}
                                    type="password"
                                    id="confirm_pwd"
                                    onChange={(e) => setMatchPwd(e.target.value)}
                                    value={matchPwd}
                                    required
                                    aria-invalid={validMatch ? "false" : "true"}
                                    aria-describedby="confirmnote"
                                    onFocus={() => setMatchFocus(true)}
                                    onBlur={() => setMatchFocus(false)}
                                />
                            </div>
                            <div className="text-center">
                                <button className="btn btn-primary" disabled={!validEmail || !validPwd || !validMatch || submitting}>
                                    {submitting && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>} Create Login
                                </button>
                            </div>
                        </form>
                        <hr />
                        <p>
                            Have an account? <Link to="/login">Sign In</Link>
                        </p>
                    </div>
                </div>
            )}
        </section>
    )
}


function Register() {
    return (
        <GoogleReCaptchaProvider reCaptchaKey="6Ld86OEUAAAAAIJEG9UDgDFR0nxnurzcp1cHxb16">
            <RegisterPage />
        </GoogleReCaptchaProvider>
    );
}

export default Register;
